/**
 * Convert a GenieCanvasNode to a React Flow Node.
 *
 * Shared utility used by CanvasDropZone and BatchGenerator
 * when programmatically creating nodes on the canvas.
 */

import type { Node } from '@xyflow/react'
import type { GenieCanvasNode } from '@/lib/canvas/types'
import { flowNodeStyle } from '@/lib/canvas/node-box'

export function toReactFlowNode(node: GenieCanvasNode, position: { x: number; y: number }): Node {
  const componentType = node.component?.type || node.type
  return {
    id: node.id,
    type: componentType,
    position,
    data: {
      state: node.component?.state || {},
      ...(node.data || {}),
      ...(node.handles ? { handles: node.handles } : {}),
    },
    style: flowNodeStyle(componentType, node.dimensions),
    ...(node.zIndex !== undefined ? { zIndex: node.zIndex } : {}),
    ...(node.parentId ? { parentId: node.parentId } : {}),
    // NOTE: Do NOT spread node.handles at the top level.
    // React Flow v12's declarative handles API expects { type: 'source'|'target' },
    // but our HandleMeta uses { type: 'input'|'output' }. Setting incompatible handles
    // at top level causes React Flow to skip DOM-measured handleBounds and use the
    // broken declarative handles instead, producing "Couldn't create edge" warnings.
    // Our handles are stored in data.handles and rendered via <NodeHandles> component.
  } as Node
}

/**
 * Calculate grid position for a child node inside a Gallery Group.
 */
export function calculateGalleryGridPosition(
  index: number,
  columns: number = 10,
  cellSize: number = 200,
  headerHeight: number = 40,
  padding: number = 20,
): { x: number; y: number } {
  return {
    x: padding + (index % columns) * cellSize,
    y: headerHeight + Math.floor(index / columns) * cellSize,
  }
}

/**
 * Calculate Gallery Group dimensions based on item count.
 */
export function calculateGalleryDimensions(
  totalItems: number,
  columns: number = 10,
  cellSize: number = 200,
  headerHeight: number = 40,
  padding: number = 20,
): { width: number; height: number } {
  const rows = Math.max(1, Math.ceil(totalItems / columns))
  return {
    width: columns * cellSize + padding * 2,
    height: headerHeight + rows * cellSize + padding,
  }
}
